import type { ICategory } from '@insights/insights-data-provider';
import type {
  ABTestExperiment,
  InitABTestClientResponse
} from '@inter-ikea-storageone/shared/analytics';
import { ABTestService } from '@inter-ikea-storageone/shared/analytics';
import { getParamOverrides } from '@inter-ikea-storageone/shared/analytics/ab/getParamOverrides';
import { initOptimizelyABTestClient } from '@inter-ikea-storageone/shared/analytics/ab/testClients/optimizely/OptimizelyABTestClient';
import { getAnalyticsConsentStatus } from '@inter-ikea-storageone/shared/cookies/getCookieConsentLevel';

export const initializeAbTestService = async () => {
  const initClient = async (): Promise<InitABTestClientResponse> => {
    const analyticsConsentStatus = await getAnalyticsConsentStatus();
    if (analyticsConsentStatus === 'ANALYTICS ALLOWED') {
      return initOptimizelyABTestClient();
    }
    return {
      success: true,
      client: {
        close: () => {
          // noop
        },
        isFeatureEnabled: (name, variation) => getParamOverrides({ name, variation }).forceEnable,
        getActiveExperiment: () => null,
        isDisconnected: () => false,
        reportEvent: () => {
          // noop
        }
      }
    };
  };
  const abTestService = new ABTestService(initClient);
  await abTestService.connect();
  return abTestService;
};

export const sendABTestEvent = async ({
  name,
  category
}: {
  name: string;
  category: ICategory;
}) => {
  try {
    const abTestService = await initializeAbTestService();
    return abTestService.sendEvent({
      name,
      category
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

export const fetchABTestExperiment = async () => {
  try {
    const abTestService = await initializeAbTestService();
    const activeExperiment = abTestService.getActiveExperiment();
    const abVersion: ABTestExperiment | null = !activeExperiment
      ? null
      : { name: activeExperiment.name, variation: activeExperiment.variation };
    return abVersion;
  } catch {
    return null;
  }
};
